import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import { ErrorPageContainer } from '@/components/error-handling/error-page-container';
import { DashboardGridSkeleton } from '@/components/data-visualization/skeletons/dashboard-grid-skeleton';
import { InstallBanner } from '@/components/pwa/install-banner';
import { AppSidebar } from '@/app/shell/app-sidebar';
import { prefetchDefaultDashboardChunk } from '@/app/shell/helpers/app-boot-splash-variants';
import { BottomNavbar } from '@/app/shell/bottom-navbar';
import { InviteTeamMemberModalContainer } from '@/features/team/components/invite-team-member-reminder-modal';
import { MainContentLoading } from '@/components/ui/main-content-loading';
import { PageTopbar } from '@/app/shell/page-topbar';
import { SidebarProvider } from '@/components/ui/sidebar/sidebar-provider';
import { PageTopbarAdornmentProvider } from '@/contexts/page-topbar-adornment/page-topbar-adornment-provider';
import { AccessGuard } from '@/features/billing/components/access-guard';
import { SubscriptionRecoveryBanner } from '@/features/billing/components/subscription-recovery/subscription-recovery-banner';

const DASHBOARD_MAIN_SKELETON_ROUTE_PREFIXES = ['/board', '/dashboard'] as const;

function shouldUseDashboardMainSkeleton(pathname: string): boolean {
  return DASHBOARD_MAIN_SKELETON_ROUTE_PREFIXES.some(routePrefix =>
    pathname.startsWith(routePrefix)
  );
}

export default function PageLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // Warm optimistic shell skips AppBootSplash — prefetch here too.
  useEffect(() => {
    prefetchDefaultDashboardChunk(location.pathname);
  }, [location.pathname]);

  // Attribution keeps pt-0 for sticky table chrome; bottom pad still clears the
  // mobile bar (64px usable + safe-area) on every route that mounts BottomNavbar.
  const isAttributionPage = location.pathname === '/board/attribution';
  const mainContentFallback = shouldUseDashboardMainSkeleton(location.pathname)
    ? <DashboardGridSkeleton showRealisticLayout={true} />
    : <MainContentLoading />;

  return (
    <SidebarProvider>
      {/* Wraps both PageTopbar and the page so routes can portal into the title row. */}
      <PageTopbarAdornmentProvider>
      {/*
        `h-dvh` instead of `h-screen` — the dynamic viewport unit excludes
        hidden Safari chrome on iOS, preventing the layout from flickering
        when the URL bar collapses on scroll.
      */}
      <div className="flex h-dvh w-full bg-background">
        {/* Sidebar renders as a fixed panel on desktop, as a Sheet drawer on mobile */}
        <AppSidebar />
        {/*
          `pt-[env(safe-area-inset-top)]` drops the top-most element below
          the iOS Dynamic Island / status bar when running as an installed
          PWA (status-bar-style: black-translucent paints the status bar
          transparently over our `bg-background`, so there's no visible gap).

          No column `gap` between the topbar and main — a gap reads as a
          separate fill band under the transparent utility chrome.

          `bg-surface-subtle dark:bg-transparent` matches the main canvas so
          transparent topbar chrome inherits the same plane as page content.
        */}
        <div className="relative flex h-full flex-1 flex-col overflow-hidden bg-surface-subtle pt-[env(safe-area-inset-top)] dark:bg-transparent">
          <SubscriptionRecoveryBanner />
          <PageTopbar />
          <main
            className={`relative z-[1] flex-1 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0${isAttributionPage ? ' pt-0' : ''}`}
            id="main-scroll-container"
          >
            <AccessGuard>
              <ErrorPageContainer fallback={mainContentFallback}>
                {children}
              </ErrorPageContainer>
            </AccessGuard>
            {/* Global Modals with Error Boundary Protection */}
            <ErrorBoundary
              fallback={null}
              onError={(error, errorInfo) => {
                console.error('Global modal error:', error, errorInfo);
              }}
            >
              <InviteTeamMemberModalContainer />
            </ErrorBoundary>
          </main>
        </div>
      </div>
      {/* Native-app style bottom navigation; renders only on mobile (< 768px). */}
      <BottomNavbar />
      {/* PWA install prompt — Android via beforeinstallprompt, iOS via Safari
          Add-to-Home-Screen instructions. Self-gates on platform, standalone
          mode, dismissal storage, and a 30s engagement floor. */}
      <InstallBanner />
      </PageTopbarAdornmentProvider>
    </SidebarProvider>
  );
}
